import first from 'lodash/first';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';

import { UsagePerDayFragment } from '@/sections/Billing/__generated__/fragments.generated';
import {
  BillingItem,
  Entity,
  OrgSharedEndpointsUsageBillingItem,
  ProductCategoryUsages,
  TrainingJobDailyUsageEntry,
  TrainingProjectUsageEntry,
} from '@/sections/Billing/types';
import { assertNever } from '@/utils/assertNever';
import Fuse from 'fuse.js';
import { produce } from 'immer';
import moment, { Moment } from 'moment';

import { DELETED_MODEL_KEYWORD } from './constants';
import {
  ChartData,
  EntityType,
  SelectedTrainingEntity,
  SelectedUsageItem,
  TrainingEntityType,
  UsageItem,
} from './types';

function getOracleName(entity: Entity): string {
  // eslint-disable-next-line no-underscore-dangle
  switch (entity.__typename) {
    case 'ModelVersionTombstone':
      return entity.modelName ?? `${DELETED_MODEL_KEYWORD} ${entity.modelId}`;
    case 'ModelVersionType':
      return entity.oracle.name;
    default:
      assertNever(entity);
  }
}

function walkTree<T extends { items: Record<string, T> }>(
  tree: Record<string, T>,
  callback: (item: T, path: string[]) => void,
  path: string[] = [],
) {
  Object.entries(tree).forEach(([key, item]) => {
    const newPath = path.concat([key]);

    callback(item, newPath);
    walkTree(item.items, callback, newPath);
  });
}

type ItemTree = {
  [key: string]: Omit<UsageItem, 'path'> & { items: ItemTree };
};

function getItemTree(items: ProductCategoryUsages['items']): ItemTree {
  const itemTree = items.reduce((ret, item) => {
    // Chain-less Oracle Versions
    // eslint-disable-next-line no-underscore-dangle
    if (item.entity.__typename === 'ModelVersionTombstone' || !item.entity.chainMetadata) {
      const oracleId =
        // eslint-disable-next-line no-underscore-dangle
        item.entity.__typename === 'ModelVersionTombstone'
          ? item.entity.modelId
          : item.entity.oracle.id;
      const oracleName = getOracleName(item.entity);
      const key = `oracle-${oracleId}`;

      ret[key] ??= {
        time: 0,
        total: 0,
        requests: 0,
        // eslint-disable-next-line no-underscore-dangle
        deleted: item.entity.__typename === 'ModelVersionTombstone' && !item.entity.modelName,
        type: EntityType.Oracle,
        name: oracleName,
        id: oracleId,
        items: {},
      };

      const oracle = ret[key];

      oracle.time += item.minutes;
      oracle.total += Number(item.cost);
      oracle.requests += Number(item.requests);

      const itemKey = `oracle-version-${item.entity.id}`;

      oracle.items[itemKey] = {
        time: item.minutes,
        total: Number(item.cost),
        requests: Number(item.requests),
        // eslint-disable-next-line no-underscore-dangle
        deleted: item.entity.__typename === 'ModelVersionTombstone',
        type: EntityType.OracleVersion,
        name:
          // eslint-disable-next-line no-underscore-dangle
          item.entity.__typename === 'ModelVersionTombstone'
            ? item.entity.id
            : item.entity.name || item.entity.id,
        id: item.entity.id,
        items: {},
      };

      return ret;
    }

    // At this point, all Oracle Versions are part of a Chain.

    const { chainId, chainName, chainDeploymentId, chainletId, chainletName } =
      item.entity.chainMetadata;

    const chainKey = `chain-${chainId}`;
    const chainDeploymentKey = `chain-deployment-${chainDeploymentId}`;
    const chainletKey = `chainlet-${chainletId}`;

    ret[chainKey] ??= {
      time: 0,
      total: 0,
      requests: 0,
      deleted: false,
      type: EntityType.Chain,
      name: chainName || chainId,
      id: chainId,
      items: {},
    };

    const chain = ret[chainKey];

    chain.items[chainDeploymentKey] ??= {
      time: 0,
      total: 0,
      requests: 0,
      deleted: false,
      type: EntityType.ChainDeployment,
      name: chainDeploymentId,
      id: chainDeploymentId,
      items: {},
    };

    const chainDeployment = chain.items[chainDeploymentKey];

    chainDeployment.items[chainletKey] = {
      time: item.minutes,
      total: Number(item.cost),
      requests: Number(item.requests),
      deleted: false,
      type: EntityType.Chainlet,
      name: chainletName || chainletId,
      id: chainletId,
      items: {},
    };

    const chainlet = chainDeployment.items[chainletKey];

    [chain, chainDeployment].forEach((chainItem) => {
      chainItem.time += chainlet.time;
      chainItem.total += chainlet.total;
      chainItem.requests += chainlet.requests;
    });

    return ret;
  }, {} as ItemTree);

  walkTree(itemTree, (item) => {
    item.items = Object.fromEntries(
      sortBy(Object.entries(item.items), [
        ([_, { total }]) => -total,
        ([_, { deleted }]) => (deleted ? 1 : 0),
      ]),
    );
  });

  return Object.fromEntries(
    sortBy(Object.entries(itemTree), [
      ([_, { total }]) => -total,
      ([_, { deleted }]) => (deleted ? 1 : 0),
    ]),
  );
}

function getItemsFromTree(tree: ItemTree): UsageItem[] {
  const treeItems: UsageItem[] = [];

  walkTree(tree, (item, path) => {
    const { items, ...rest } = item;

    treeItems.push({
      ...rest,
      path,
    });
  });

  return treeItems;
}

function getItemFilter(selectedItem: SelectedUsageItem): (item: BillingItem) => boolean {
  switch (selectedItem.type) {
    case EntityType.Chain:
      return ({ entity }) =>
        // eslint-disable-next-line no-underscore-dangle
        entity.__typename === 'ModelVersionType' &&
        entity.chainMetadata?.chainId === selectedItem.id;
    case EntityType.ChainDeployment:
      return ({ entity }) =>
        // eslint-disable-next-line no-underscore-dangle
        entity.__typename === 'ModelVersionType' &&
        entity.chainMetadata?.chainDeploymentId === selectedItem.id;
    case EntityType.Chainlet:
      return ({ entity }) =>
        // eslint-disable-next-line no-underscore-dangle
        entity.__typename === 'ModelVersionType' &&
        entity.chainMetadata?.chainletId === selectedItem.id;
    case EntityType.Oracle:
      return ({ entity }) =>
        selectedItem.id ===
        // eslint-disable-next-line no-underscore-dangle
        (entity.__typename === 'ModelVersionType' ? entity.oracle.id : entity.modelId);
    case EntityType.OracleVersion:
      return ({ entity }) => entity.id === selectedItem.id;
    default:
      assertNever(selectedItem.type);
  }
}

function calcTotalDataPerDay(items: BillingItem[]): UsagePerDayFragment[] {
  const totalMap = items.reduce((acc, item) => {
    item.usagePerDay.forEach((usage) => {
      if (acc.has(usage.date)) {
        const prevData = acc.get(usage.date);
        acc.set(usage.date, {
          cost: prevData.cost + Number(usage.cost),
          minutes: prevData.minutes + Number(usage.minutes),
          requests: prevData.requests + Number(usage.requests),
        });
      } else {
        acc.set(usage.date, {
          cost: Number(usage.cost),
          minutes: Number(usage.minutes),
          requests: Number(usage.requests),
        });
      }
    });
    return acc;
  }, new Map());

  const totalDataPerDay = Array.from(totalMap, ([date, { cost, minutes, requests }]) => ({
    date,
    cost,
    minutes,
    requests,
  }));

  // Sort the array by date
  return totalDataPerDay.sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf());
}

const DEFAULT_TOTAL_VALUES_FOR_SHARED_ENDPOINTS_USAGE = {
  totalCost: 0,
  totalInputTokens: 0,
  totalOutputTokens: 0,
};

function calcTotalValuesForSharedEndpoints(items?: OrgSharedEndpointsUsageBillingItem[]) {
  if (!items) {
    return DEFAULT_TOTAL_VALUES_FOR_SHARED_ENDPOINTS_USAGE;
  }

  return items.reduce(
    (acc, item) => {
      acc.totalCost += Number(item.cost);
      acc.totalInputTokens += item.usage.numInputTokens;
      acc.totalOutputTokens += item.usage.numOutputTokens;
      return acc;
    },
    { ...DEFAULT_TOTAL_VALUES_FOR_SHARED_ENDPOINTS_USAGE },
  );
}

function calcTotalCost(items: BillingItem[]): number {
  return items.reduce((acc, item) => acc + Number(item.cost), 0);
}

function calcTotalRequests(items: BillingItem[]): number {
  return items.reduce((acc, item) => acc + Number(item.requests), 0);
}

function calcTotalMinutes(items: BillingItem[]): number {
  return items.reduce((acc, item) => acc + Number(item.minutes), 0);
}

function filterItemTree(itemTree: ItemTree, query: string, fuse: Fuse<UsageItem>) {
  if (!query) {
    return itemTree;
  }

  const resultPaths = new Set<string>();

  fuse
    .search(query)
    .map((result) => result.item)
    .forEach(({ path }) => {
      for (let i = 1; i <= path.length; i += 1) {
        resultPaths.add(path.slice(0, i).join('/'));
      }
    });

  return produce(itemTree, (draft) => {
    Object.keys(draft).forEach((key) => {
      if (!resultPaths.has(key)) {
        delete draft[key];
      }
    });

    walkTree(draft, (item, path) => {
      item.items = pickBy(item.items, (_, key) => resultPaths.has(path.concat([key]).join('/')));
    });
  });
}

function getRange(startDate: string | Moment, endDate: string | Moment) {
  const fromDate = moment(startDate);
  const toDate = moment(endDate);
  const diff = toDate.diff(fromDate, 'days');

  return Array.from({ length: diff + 1 }).map((_, i) =>
    fromDate.clone().add(i, 'days').format('YYYY-MM-DD'),
  );
}

function genChartData(totalDataPerDay: UsagePerDayFragment[], range: string[]): ChartData {
  return range.reduce(
    (acc, date) => {
      const { costDatapoints, requestsDatapoints, labels, tooltipData } = acc;
      const item = totalDataPerDay.find((item) => item.date === date);
      const label = moment(date).format('MM/DD');

      if (item) {
        costDatapoints.push(item.cost);
        requestsDatapoints.push(item.requests);
        tooltipData.push({
          cost: item.cost,
          timestamp: date,
          requests: item.requests,
          minutes: item.minutes,
        });
      } else {
        tooltipData.push({
          cost: 0,
          timestamp: date,
          requests: 0,
          minutes: 0,
        });
        costDatapoints.push(0);
        requestsDatapoints.push(0);
      }

      labels.push(label);

      return acc;
    },
    {
      costDatapoints: [],
      requestsDatapoints: [],
      labels: [],
      tooltipData: [],
    },
  );
}

function getChartDataForSharedEndpoints(
  items: OrgSharedEndpointsUsageBillingItem[] = [],
  range: string[],
) {
  return range.reduce(
    (acc, date) => {
      const { costDatapoints, labels, tooltipData } = acc;
      const item = items
        .filter((item) => first(item.date.split('T')) === date)
        .reduce(
          (acc, item) => {
            acc.cost += Number(item.cost);

            return acc;
          },
          { cost: 0 },
        );
      const label = moment(date).format('MM/DD');

      if (item) {
        costDatapoints.push(item.cost);
        tooltipData.push({
          cost: item.cost,
          timestamp: date,
        });
      } else {
        tooltipData.push({
          cost: 0,
          timestamp: date,
        });
        costDatapoints.push(0);
      }

      labels.push(label);

      return acc;
    },
    {
      costDatapoints: [],
      labels: [],
      tooltipData: [],
    },
  );
}

function getChartDataForTraining(items: TrainingJobDailyUsageEntry[], range: string[]) {
  return range.reduce(
    (acc, date) => {
      const { costDatapoints, labels, tooltipData } = acc;
      const item = items
        .filter((item) => first(item.date.split('T')) === date)
        .reduce(
          (acc, item) => {
            acc.cost += item.cost;
            acc.minutes += item.minutes;

            return acc;
          },
          { cost: 0, minutes: 0 },
        );
      const label = moment(date).format('MM/DD');

      if (item) {
        costDatapoints.push(item.cost);
        tooltipData.push({
          cost: item.cost,
          minutes: item.minutes,
          timestamp: date,
        });
      } else {
        tooltipData.push({
          cost: 0,
          minutes: 0,
          timestamp: date,
        });
        costDatapoints.push(0);
      }

      labels.push(label);

      return acc;
    },
    {
      costDatapoints: [],
      labels: [],
      tooltipData: [],
    },
  );
}

function getBillingPeriodForEmptyState(): [Moment, Moment] {
  const today = moment();

  return [today.clone().subtract(1, 'months'), today];
}

function isSelectedTrainingEntityValid(
  selectedTrainingEntity: SelectedTrainingEntity,
  projectUsageById: Record<string, TrainingProjectUsageEntry>,
) {
  const key =
    selectedTrainingEntity.type === TrainingEntityType.TrainingProject
      ? selectedTrainingEntity.id
      : selectedTrainingEntity.projectId;

  return key in projectUsageById;
}

export {
  getItemTree,
  getItemsFromTree,
  ItemTree,
  calcTotalDataPerDay,
  calcTotalCost,
  calcTotalMinutes,
  calcTotalRequests,
  getItemFilter,
  filterItemTree,
  getRange,
  genChartData,
  getBillingPeriodForEmptyState,
  calcTotalValuesForSharedEndpoints,
  getChartDataForSharedEndpoints,
  getChartDataForTraining,
  isSelectedTrainingEntityValid,
};
